import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { policyService } from '../../services/supabase';
import type { Policy, PolicyDocument } from '../../types';
import type { Database } from '../../types/supabase';
import { v4 as uuidv4 } from 'uuid';

type PolicyRow = Database['public']['Tables']['policies']['Row'];

const mapPolicyFromRow = (row: PolicyRow): Policy => ({
  id: row.id,
  title: row.title,
  content: row.content,
  status: row.status,
  department: row.department,
  tags: row.tags,
  created_at: row.created_at,
  updated_at: row.updated_at,
  created_by: row.created_by,
  organization_id: row.organization_id,
  approved_by: (row as { approved_by?: string | null }).approved_by ?? null,
  approved_at: (row as { approved_at?: string | null }).approved_at ?? null,
  document_path: row.document_path,
  document: row.document_path
    ? {
        id: uuidv4(),
        title: `${row.title} Document`,
        content: '', // Assuming content is empty
        version: '1.0',
        tags: [],
        status: 'draft',
        createdAt: row.created_at,
        updatedAt: row.updated_at,
        authorId: row.created_by,
        fileName: row.document_path.split('/').pop() || '',
        fileSize: 0, // This will be updated when document is loaded
        fileType: '', // This will be updated when document is loaded
        uploadDate: row.updated_at,
        lastModified: row.updated_at,
        url: row.document_path,
      } as PolicyDocument
    : undefined,
  lastModified: row.updated_at,
});

interface PoliciesState {
  items: Policy[];
  loading: boolean;
  error: string | null;
  selectedPolicy: Policy | null;
}

const initialState: PoliciesState = {
  items: [],
  loading: false,
  error: null,
  selectedPolicy: null,
};

// Async thunks
export const fetchPolicies = createAsyncThunk(
  'policies/fetchPolicies',
  async (organizationId: string) => {
    console.log('Fetching policies for organization:', organizationId);
    const policies = await policyService.getByOrganization(organizationId);
    console.log('Fetched policies:', policies);
    return policies.map(mapPolicyFromRow);
  }
);

export const createPolicy = createAsyncThunk(
  'policies/createPolicy',
  async (policy: Omit<PolicyRow, 'id' | 'created_at' | 'updated_at'>) => {
    console.log('Creating policy:', policy);
    const newPolicy = await policyService.create(policy);
    console.log('Created policy:', newPolicy);
    return mapPolicyFromRow(newPolicy);
  }
);

export const updatePolicy = createAsyncThunk(
  'policies/updatePolicy',
  async ({ id, updates }: { id: string; updates: Partial<PolicyRow> }) => {
    console.log('Updating policy:', { id, updates });
    const updatedPolicy = await policyService.update(id, updates);
    console.log('Updated policy:', updatedPolicy);
    return mapPolicyFromRow(updatedPolicy);
  }
);

export const deletePolicy = createAsyncThunk(
  'policies/deletePolicy',
  async (id: string) => {
    console.log('Deleting policy:', id);
    await policyService.delete(id);
    console.log('Deleted policy:', id);
    return id;
  }
);

const policiesSlice = createSlice({
  name: 'policies',
  initialState,
  reducers: {
    selectPolicy: (state, action) => {
      state.selectedPolicy = action.payload;
    },
    clearSelectedPolicy: (state) => {
      state.selectedPolicy = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch policies
      .addCase(fetchPolicies.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPolicies.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchPolicies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch policies';
      })
      // Create policy
      .addCase(createPolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.items.push(action.payload);
      })
      .addCase(createPolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create policy';
      })
      // Update policy
      .addCase(updatePolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePolicy.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(
          (policy) => policy.id === action.payload.id
        );
        if (index !== -1) {
          state.items[index] = action.payload;
          if (state.selectedPolicy?.id === action.payload.id) {
            state.selectedPolicy = action.payload;
          }
        }
      })
      .addCase(updatePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update policy';
      })
      // Delete policy
      .addCase(deletePolicy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePolicy.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter((policy) => policy.id !== action.payload);
        if (state.selectedPolicy?.id === action.payload) {
          state.selectedPolicy = null;
        }
      })
      .addCase(deletePolicy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete policy';
      });
  },
});

export const { selectPolicy, clearSelectedPolicy } = policiesSlice.actions;

export default policiesSlice.reducer;
